import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Stethoscope, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const dashRoute = (role) => (role === "institute" ? "/institute" : "/dashboard");

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("professional");
  const [form, setForm] = useState({ name: "", email: "", password: "", profession: "", city: "", institute_name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await register({ ...form, role });
      toast.success(`Welcome to Medlane, ${u.name}`);
      nav(dashRoute(u.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden bg-accent lg:block">
        <div className="flex h-full flex-col justify-center p-16 text-accent-foreground">
          <h2 className="font-heading text-4xl font-extrabold leading-tight">Join India's healthcare learning network.</h2>
          <p className="mt-4 max-w-md text-accent-foreground/80">Whether you're a professional seeking growth or an institute filling seats — Medlane connects you.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-6 inline-block">
            <Logo className="h-9" />
          </Link>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Create your account</h1>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {[{ v: "professional", icon: Stethoscope, l: "Professional", d: "Doctor, Nurse, Tech" },
              { v: "institute", icon: Building2, l: "Institute", d: "List your courses" }].map((r) => (
              <button key={r.v} type="button" data-testid={`role-select-${r.v}`} onClick={() => setRole(r.v)}
                className={`rounded-xl border-2 p-4 text-left transition-all ${role === r.v ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground"}`}>
                <r.icon className={`h-5 w-5 ${role === r.v ? "text-primary" : "text-muted-foreground"}`} />
                <p className="mt-2 font-semibold">{r.l}</p>
                <p className="text-xs text-muted-foreground">{r.d}</p>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name">{role === "institute" ? "Contact name" : "Full name"}</Label>
              <Input id="name" data-testid="register-name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
            </div>
            {role === "institute" && (
              <div className="rounded-xl border border-border bg-secondary/40 p-3 text-xs text-muted-foreground" data-testid="institute-claim-hint">
                After signing up, you'll <span className="font-semibold text-foreground">claim your institute</span> (and the courses our team has already added) from your portal.
              </div>
            )}
            {role === "professional" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="prof">Profession</Label>
                  <Input id="prof" data-testid="register-profession" value={form.profession} onChange={(e) => set("profession", e.target.value)} placeholder="MBBS, Nurse..." />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input id="city" data-testid="register-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Ahmedabad" />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="register-email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="register-password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} />
            </div>
            {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" data-testid="register-submit" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="font-semibold text-primary hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
